import React, {useEffect, useState} from "react";
import {useSearchParams, useNavigate} from "react-router-dom";
import {Card, Col, Row, Spinner} from "reactstrap";
import {VsiBubble, VsiH4,} from "../../../styled/App.styled";
import { useVsiAppContext} from "../../../stores/VsiApp";
import Grid from "../../../components/Grid";
import {enterpriseHostDefs} from "../../../utils/gridColumnDefs/hosts/hostSummary";
import {getReport} from "../../../utils/Verbs";
import {useTitleFromComponent} from "../../../utils/hooks";
import DonutChart from "../../../components/DonutChart";
import VerticalBarChart from "../../../components/VerticalBarChart";

export default function EntHostSummary({context, title}) {
    useTitleFromComponent(`${context} / ${title}`);
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [hostsGrid, setHostsGrid] = useState([]);
    const [hostsPie, setHostsPie] = useState({});
    const [provPieInfo, setProvPieInfo] = useState([]);
    const [hostsBar, setHostsBar] = useState({});
    const [xAxis, setXAxis] = useState([]);

    let navigate = useNavigate();

    const latencyTypes = ['No latency', '0.1 - 1ms', '1 - 2ms', 'Over 2ms'];

    const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();

    const getEnterpriseHostSummary = async function () {
        const route = `/api/v6/report/enterprise_host_summary_proc?client_id=${client.clientId}&period_id=${periodId}`;

        try {
            const token = await getAccessTokenSilently();
            const data = await getReport(route, null, token);
            const narrowData = data?.data?.recordset;

            setHostsGrid(narrowData || []);
            getVisualInfo(narrowData || []);
            setXAxis(latencyTypes);
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            setIsLoading(false)
        }
    };

    const getVisualInfo = (data) => {
        pieDataConfig(data);
        filterLatencyLuns(data);
        filterProvisioningType(data);
    }

    const pieDataConfig = (data) => {
        let lowIops = 0;
        let midIops = 0;
        let highIops = 0;
        let maxIops = 0;
        data.forEach(datum => {
            if (datum.iops < 100) {
                lowIops = lowIops + 1;
            } else if (datum.iops >= 100 && datum.iops <= 500) {
                midIops = midIops + 1;
            } else if (datum.iops > 500 && datum.iops <= 1000) {
                highIops = highIops + 1;
            } else if (datum.iops > 1000) {
                maxIops = maxIops + 1;
            } else {
                return;
            }
        });

        setHostsPie([
            {
                name: "< 100 IOPs",
                y: parseFloat(lowIops.toFixed(2))
            },
            {
                name: "101 - 500 IOPs",
                y: parseFloat(midIops.toFixed(2))
            },
            {
                name: "501 - 1000 IOPs",
                y: parseFloat(highIops.toFixed(2))
            },
            {
                name: "> 1000 IOPs",
                y: parseFloat(maxIops.toFixed(2))
            }
        ]);
    };

    const filterProvisioningType = (data) => {
        let thin = 0;
        let thick = 0;
        let compressed = 0;
        data.forEach(datum => {
            if (datum.provisioning_type === 'Thin') {
                thin++;
            } else if (datum.provisioning_type === 'Thick') {
                thick++;
            } else if (datum.provisioning_type === 'Compressed') {
                compressed++;
            } else {
                console.error("Something went wrong.");
                return;
            }
        });

        setProvPieInfo([
            {
                name: "Thin",
                y: thin
            },
            {
                name: "Thick",
                y: thick
            },
            {
                name: "Compressed",
                y: compressed
            }
        ]);
    };

    const filterLatencyLuns = (data) => {
        let filteredData = data.map(host => {
            if (host.latency === 0 || !host.latency) {
                return {title: "No latency", lunCount: host.luns_attached}
            } else if (host.latency > 0 && host.latency <= 1) {
                return {title: "0.1 - 1ms", lunCount: host.luns_attached}
            } else if (host.latency > 1 && host.latency <= 2) {
                return {title: "1.1 - 2ms", lunCount: host.luns_attached}
            } else if (host.latency > 2) {
                return {title: "Over 2ms", lunCount: host.luns_attached}
            } else {
                console.error("Not a number");
                return null;
            }
        });

        let noLat = 0;
        let lowLat = 0;
        let midLat = 0;
        let highLat = 0;
        filteredData.forEach(datum => {
            if (datum.title === 'No latency') {
                noLat = noLat + datum.lunCount;
            } else if (datum.title === '0.1 - 1ms') {
                lowLat = lowLat + datum.lunCount;
            } else if (datum.title === '1.1 - 2ms') {
                midLat = midLat + datum.lunCount;
            } else if (datum.title === "Over 2ms") {
                highLat = highLat + datum.lunCount
            } else {
                console.error("Something went wrong.");
                return;
            }
        });

        let chartInfo = [
            {
                name: "Latency",
                data: [noLat, lowLat, midLat, highLat]
            }
        ];

        setHostsBar(chartInfo);
    };

    useEffect(() => {
        getEnterpriseHostSummary();
    }, [client, periodId, searchParams]);

    const onRowClick = (e) => {
        navigate(`/reports/hosts/host/host-trends?host_name=${encodeURI(e.data.host_name)}&device_name=${encodeURI(e.data.device_name)}`)
    };

    const filterChanged = (eventData) => {
        let rowData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            rowData.push(node.data);
        });
        getVisualInfo(rowData);
    }

    // First piechart will use provisioning_type (thick/thin/compressed)

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Enterprise Host Summary</VsiH4>
            {isLoading ? <Spinner color={"primary"}/> : (
                <div>
                    <Row>
                        <Col xs={12} lg={4}>
                            <VsiBubble autobotName='ProvisioningDonutChart'>
                                    <DonutChart data={provPieInfo || []}
                                                height={450}
                                                title={"Provisioning"}
                                                seriesName={"Count"}
                                                enableLegend={true}/>
                            </VsiBubble>
                        </Col>
                        <Col xs={12} lg={4}>
                            <VsiBubble autobotInfo={{ yValue: 'Count of LUNs' }}>
                                    <VerticalBarChart
                                        categories={xAxis}
                                        series={hostsBar}
                                        changeAxisTitle={true}
                                        yTitle1={"Count of LUNs"}
                                        height={450}
                                        axisMaxMin={{axis1Min: 0}}
                                    />
                            </VsiBubble>
                        </Col>
                        <Col xs={12} lg={4}>
                            <VsiBubble autobotName='IopsDonutChart'>
                                    <DonutChart data={hostsPie || []}
                                                title={"IOPs"}
                                                seriesName={"Host Count"}
                                                height={450}
                                                enableLegend={true}/>
                            </VsiBubble>
                        </Col>
                    </Row>
                    <VsiBubble>
                        <Grid addStyle data={hostsGrid}
                              gridText={'Double click to see detailed host reports'}
                              columnDefs={enterpriseHostDefs}
                              filterChanged={filterChanged}
                              rowDoubleClicked={onRowClick}/>
                    </VsiBubble>
                </div>
            )}
        </Card>
    )
}
